import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api, { apiError } from "@/lib/apiClient";
import { Search, GitBranch, Loader2, Lock, ArrowRight, Plug, Rocket } from "lucide-react";
import { toast } from "sonner";

export default function NewProject() {
  const navigate = useNavigate();
  const [conns, setConns] = useState(null);
  const [repos, setRepos] = useState(null);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState("");
  const [name, setName] = useState("");
  const [buildCmd, setBuildCmd] = useState("");
  const [publishDir, setPublishDir] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api.get("/connections").then((r) => setConns(r.data)).catch(() => setConns({}));
  }, []);

  const ready = conns?.github?.connected && conns?.netlify?.connected;

  useEffect(() => {
    if (!ready) return;
    setRepos(null);
    api.get("/github/repos").then((r) => setRepos(r.data)).catch((e) => { toast.error(apiError(e)); setRepos([]); });
  }, [ready]);

  const filtered = useMemo(() => {
    if (!repos) return [];
    const q = query.toLowerCase();
    return repos.filter((r) => r.full_name.toLowerCase().includes(q)).slice(0, 40);
  }, [repos, query]);

  const pickRepo = async (repo) => {
    setSelected(repo);
    setName(repo.name);
    setBranch(repo.default_branch);
    setBranches([repo.default_branch]);
    try {
      const r = await api.get(`/github/repos/${repo.owner}/${repo.name}/branches`);
      setBranches(r.data.length ? r.data : [repo.default_branch]);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const create = async () => {
    setCreating(true);
    try {
      const r = await api.post("/projects", {
        name,
        repo_owner: selected.owner,
        repo_name: selected.name,
        repo_id: selected.id,
        branch,
        build_command: buildCmd || null,
        publish_directory: publishDir || null,
      });
      toast.success("Site created — triggering first build");
      await api.post(`/projects/${r.data.id}/deploy`).catch(() => {});
      navigate(`/projects/${r.data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCreating(false);
    }
  };

  return (
    <Layout>
      <div className="mb-8">
        <Link to="/" className="text-xs text-neutral-500 hover:text-white transition-colors duration-200">← Projects</Link>
        <h1 className="font-heading text-3xl md:text-4xl font-medium tracking-tighter mt-3">New deployment</h1>
        <p className="text-sm text-neutral-400 mt-2">Pick a repo, set build options, and deploy to Netlify.</p>
      </div>

      {conns && !ready && (
        <div className="rounded-lg border border-[#262626] bg-[#141414] p-8 text-center max-w-lg" data-testid="connect-first">
          <Plug className="h-8 w-8 text-neutral-400 mx-auto mb-4" strokeWidth={1.5} />
          <h3 className="font-heading text-xl font-medium mb-2">Connect your accounts first</h3>
          <p className="text-sm text-neutral-400 mb-2">
            GitHub: {conns.github?.connected ? "✓ connected" : "not connected"} · Netlify: {conns.netlify?.connected ? "✓ connected" : "not connected"}
          </p>
          <Link to="/connections" className="inline-flex items-center gap-2 rounded-md bg-white text-black px-4 py-2 text-sm font-medium hover:bg-neutral-200 transition-colors duration-200 mt-4">
            Go to Connections <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      )}

      {ready && (
        <div className="grid lg:grid-cols-5 gap-8">
          {/* Repo picker */}
          <div className="lg:col-span-3">
            <div className="rounded-lg border border-[#262626] bg-[#141414] overflow-hidden">
              <div className="p-4 border-b border-[#262626]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500" />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    data-testid="repo-search-input"
                    placeholder="Search repositories..."
                    className="w-full h-10 rounded-md border border-[#262626] bg-[#0A0A0A] pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-neutral-500"
                  />
                </div>
              </div>
              <div className="max-h-[480px] overflow-y-auto terminal-scroll">
                {repos === null && (
                  <div className="p-8 flex items-center justify-center text-neutral-500 text-sm gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading repositories…
                  </div>
                )}
                {repos && filtered.length === 0 && (
                  <div className="p-8 text-center text-neutral-500 text-sm">No repositories found</div>
                )}
                {filtered.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => pickRepo(r)}
                    data-testid={`repo-item-${r.id}`}
                    className={`w-full flex items-center justify-between px-4 py-3 text-left border-b border-[#1a1a1a] transition-colors duration-200 ${selected?.id === r.id ? "bg-[#1F1F1F]" : "hover:bg-[#181818]"}`}
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate flex items-center gap-2">
                        {r.private && <Lock className="h-3 w-3 text-neutral-500" />}
                        {r.full_name}
                      </div>
                      <div className="text-xs text-neutral-500 font-mono">{r.default_branch}</div>
                    </div>
                    {selected?.id === r.id && <span className="text-xs text-emerald-400 font-medium">Selected</span>}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Config */}
          <div className="lg:col-span-2">
            <div className="rounded-lg border border-[#262626] bg-[#141414] p-6 sticky top-8">
              <h3 className="font-heading text-lg font-medium mb-4">Configure</h3>
              {!selected ? (
                <p className="text-sm text-neutral-500">Select a repository to configure the deployment.</p>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-neutral-400 mb-1.5">Site name</label>
                    <input value={name} onChange={(e) => setName(e.target.value)} data-testid="site-name-input"
                      className="w-full h-10 rounded-md border border-[#262626] bg-[#0A0A0A] px-3 text-sm outline-none focus:ring-2 focus:ring-neutral-500" />
                  </div>
                  <div>
                    <label className="block text-xs text-neutral-400 mb-1.5 flex items-center gap-1.5"><GitBranch className="h-3 w-3" /> Branch</label>
                    <select value={branch} onChange={(e) => setBranch(e.target.value)} data-testid="branch-select"
                      className="w-full h-10 rounded-md border border-[#262626] bg-[#0A0A0A] px-3 text-sm outline-none focus:ring-2 focus:ring-neutral-500">
                      {branches.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-neutral-400 mb-1.5">Build command <span className="text-neutral-600">(auto-detect if blank)</span></label>
                    <input value={buildCmd} onChange={(e) => setBuildCmd(e.target.value)} data-testid="build-command-input" placeholder="npm run build"
                      className="w-full h-10 rounded-md border border-[#262626] bg-[#0A0A0A] px-3 text-sm font-mono outline-none focus:ring-2 focus:ring-neutral-500" />
                  </div>
                  <div>
                    <label className="block text-xs text-neutral-400 mb-1.5">Publish directory <span className="text-neutral-600">(auto-detect if blank)</span></label>
                    <input value={publishDir} onChange={(e) => setPublishDir(e.target.value)} data-testid="publish-dir-input" placeholder="dist"
                      className="w-full h-10 rounded-md border border-[#262626] bg-[#0A0A0A] px-3 text-sm font-mono outline-none focus:ring-2 focus:ring-neutral-500" />
                  </div>
                  <button
                    onClick={create}
                    disabled={creating || !name}
                    data-testid="deploy-button"
                    className="w-full h-11 rounded-md bg-white text-black text-sm font-medium hover:bg-neutral-200 transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
                    {creating ? "Creating site…" : "Deploy to Netlify"}
                  </button>
                  <p className="text-xs text-neutral-600 leading-relaxed">
                    A Netlify deploy key is added to your repo automatically so Netlify can build it.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
